// Temporary filtering prototype; remove before production (see CHANGELOG.md).
import { Search } from './search.js';
import { DEMO_TERMS, demoSearch } from './search-demo-filters.js';

class SearchDemo extends Search {
    async init() {
        this.selected = new Set();
        this.terms = document.getElementById('search-demo-terms');
        this.clear = document.getElementById('search-demo-clear');
        this.status = document.getElementById('search-demo-status');
        this.events = new AbortController();
        this.fadeTimers = new Map();
        await this.loadData();
        if (this._destroyed || !this.input.isConnected) return;

        this._randomSet = [...this.data];
        for (let i = this._randomSet.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [this._randomSet[i], this._randomSet[j]] = [this._randomSet[j], this._randomSet[i]];
        }
        this.buttons = new Map(DEMO_TERMS.map(term => {
            const button = document.createElement('button');
            button.type = 'button';
            button.className = 'search-demo-term';
            button.dataset.searchTerm = term;
            button.setAttribute('aria-pressed', 'false');
            const label = document.createElement('span');
            label.textContent = term;
            const count = document.createElement('span');
            count.className = 'search-demo-count';
            button.append(label, ' ', count);
            this.terms.appendChild(button);
            return [term, { button, count }];
        }));
        this.bindEvents();
        this.search(this.input.value.trim());
        this.terms.setAttribute('aria-busy', 'false');
    }

    bindEvents() {
        const options = { signal: this.events.signal };
        this.input.addEventListener('input', () => {
            clearTimeout(this.debounce);
            this.debounce = setTimeout(() => this.search(this.input.value.trim()), 150);
        }, options);
        this.terms.addEventListener('click', event => {
            const button = event.target.closest('button[data-search-term]');
            if (!button || button.disabled) return;
            const term = button.dataset.searchTerm;
            if (this.selected.has(term)) this.selected.delete(term);
            else this.selected.add(term);
            clearTimeout(this.debounce);
            this.search(this.input.value.trim());
        }, options);
        this.clear.addEventListener('click', () => {
            this.selected.clear();
            clearTimeout(this.debounce);
            this.search(this.input.value.trim());
            this.input.focus();
        }, options);
        this.container.addEventListener('click', event => {
            if (event.target.closest('a')) return;
            const card = event.target.closest('[data-wall-link]');
            if (card) window.location.href = card.dataset.wallLink;
        }, options);
    }

    search(query) {
        if (this._destroyed || !this.container.isConnected) return;
        const { results, counts } = demoSearch(this._randomSet, query, this.selected);
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        for (const [term, { button, count }] of this.buttons) {
            const selected = this.selected.has(term);
            const available = selected || counts.get(term) > 0;
            count.textContent = `(${counts.get(term)})`;
            button.setAttribute('aria-pressed', String(selected));
            if (!this.ready || available !== !button.disabled) {
                clearTimeout(this.fadeTimers.get(button));
                this.fadeTimers.delete(button);
                button.disabled = !available;
                button.classList.toggle('is-unavailable', !available);
                if (available) {
                    button.hidden = false;
                } else if (!this.ready || reduceMotion) {
                    button.hidden = true;
                } else {
                    this.fadeTimers.set(button, setTimeout(() => {
                        button.hidden = true;
                        this.fadeTimers.delete(button);
                    }, 180));
                }
            }
        }
        this.ready = true;
        this.clear.hidden = this.selected.size === 0;
        this.status.textContent = `${results.length} ${results.length === 1 ? 'result' : 'results'}`;
        const ids = results.map(item => item.id).join(',');
        if (ids === this.lastResultIds) return;
        this.lastResultIds = ids;
        this.renderer.render(results, this.container);
    }

    destroy() {
        this._destroyed = true;
        this.events.abort();
        clearTimeout(this.debounce);
        for (const timer of this.fadeTimers.values()) clearTimeout(timer);
        this.fadeTimers.clear();
        this.renderer.destroy();
    }
}

let currentDemo;
export function init() {
    currentDemo?.destroy();
    currentDemo = document.getElementById('search-demo-terms') ? new SearchDemo() : null;
}
